import { useRef, useState } from 'react'
import { Box, Button, ProgressBar, Stack, Text } from '@ui'
import { getJSON } from '../api.js'
import { formatDate } from '../format.js'
import { findDuplicate } from '../photoMatch.js'
import { ACCEPT, readTakenAt, uploadToWalk } from '../upload.js'

// "Add photos or videos" on the walk page: picks files, uploads them one at a time (see
// upload.js) with a progress line each, and calls onAdded after each one so the gallery updates.
// A file already on the site, or deleted from it before, is skipped (the same check as the bulk
// upload's, photoMatch.js), with "Upload anyway" on its line.

// Why a file is skipped, or null to upload it. The lists are /api/photos and /api/photos/deleted.
async function skipReason(file, walkId, photos, deleted) {
  const takenAt = await readTakenAt(file).catch(() => null)
  const live = findDuplicate(file, takenAt, photos)
  if (live) return live.walk_id === walkId ? 'Already on this walk' : `Already on the site (walk of ${formatDate(live.start_time)})`
  const gone = findDuplicate(file, takenAt, deleted)
  if (gone) return `${gone.purged ? 'Permanently deleted' : 'Deleted'} from the site on ${formatDate(gone.deleted_at)}`
  return null
}
export default function PhotoUpload({ walkId, onAdded }) {
  const input = useRef(null)
  const [items, setItems] = useState([]) // [{ key, name, stage, progress, error, skipped, warning }]
  const busy = items.some((i) => !i.error && !i.skipped && i.stage !== 'Added')

  const update = (key, change) => setItems((list) => list.map((i) => (i.key === key ? { ...i, ...change } : i)))

  const uploadOne = async (key, file) => {
    update(key, { skipped: null, stage: 'Waiting' })
    try {
      const { warning } = await uploadToWalk(walkId, file, {
        onStage: (stage) => update(key, { stage }),
        onProgress: (progress) => update(key, { progress }),
      })
      update(key, { stage: 'Added', progress: 1, warning })
      onAdded?.()
    } catch (err) {
      update(key, { error: err.message })
    }
  }

  const start = async (files) => {
    const queued = [...files].map((file, n) => ({ key: `${Date.now()}-${n}`, file, name: file.name, stage: 'Checking', progress: 0 }))
    setItems((list) => [...list.filter((i) => !i.error && !i.skipped && i.stage !== 'Added'), ...queued])
    // Fetched fresh each time, so photos added a moment ago count. If the check can't be made,
    // the files upload as before.
    const [photos, deleted] = await Promise.all([getJSON('/api/photos'), getJSON('/api/photos/deleted')]).catch(() => [[], []])
    for (const { key, file } of queued) {
      const skipped = await skipReason(file, walkId, photos, deleted)
      if (skipped) update(key, { skipped })
      else await uploadOne(key, file)
    }
  }

  return (
    <Box mt={2}>
      <input
        ref={input} type="file" accept={ACCEPT} multiple hidden
        onChange={(e) => { if (e.target.files.length) start(e.target.files); e.target.value = '' }}
      />
      <Button size="sm" variant="secondary" outline onClick={() => input.current.click()} disabled={busy}>
        Add photos or videos
      </Button>
      {items.length > 0 && (
        <Box as="ul" unstyledList text="small" mt={2} mb={0} className="photo-uploads">
          {items.map((i) => (
            <Box as="li" key={i.key} mb={1}>
              <Stack gap={2}>
                <Text truncate>{i.name}</Text>
                {i.skipped ? (
                  <Text ms="auto" textAlign="end" muted>
                    Skipped: {i.skipped}
                    <Button variant="link" size="sm" p={0} ms={2} valign="baseline" disabled={busy} onClick={() => uploadOne(i.key, i.file)}>
                      Upload anyway
                    </Button>
                  </Text>
                ) : (
                  <Text ms="auto" nowrap muted={!i.error} tone={i.error ? 'danger' : undefined}>
                    {i.error ? `Failed: ${i.error}` : i.stage === 'Uploading' ? `${Math.round(i.progress * 100)}%` : i.stage}
                  </Text>
                )}
              </Stack>
              {!i.error && i.stage === 'Uploading' && <ProgressBar value={i.progress * 100} thin />}
              {i.warning && <Box tone="warning">{i.warning}</Box>}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}
